// Single source of truth for how a pregnancy was conceived, shared by the FHQ pregnancy form
// and the pedigree editor so the two surfaces can never drift (and neither can invent a donor).
//
// The UI captures TWO independent axes:
//   - partner: a spouse reference (FamilyChanged _id / pid), or null for "no partner"
//   - donors:  sperm donor? and/or egg donor? (independent booleans)
// From those, deriveConception produces the persisted record fields (Bennett):
//   conceivedWith ∈ '' | 'partner' | 'spermDonor' | 'eggDonor' | 'embryoDonor' | 'partnerAndDonor'
//   donors[]      = the gamete-donor satellites
//   otherParentMemberId = the in-graph spouse (only when partner-conceived)
//
// PRINCIPLE: nothing defaults to a donor. With no partner and no donor selected, conceivedWith
// is '' (unspecified) — the pregnancy renders carrier-alone. A donor appears ONLY when the user
// explicitly ticks it.

const mkDonor = (role) => ({ role, anonymous: true });

export function deriveConception({
  spouseRef = null,
  spermDonor = false,
  eggDonor = false
} = {}) {
  const donors = [];
  if (eggDonor) donors.push(mkDonor('ovum'));
  if (spermDonor) donors.push(mkDonor('sperm'));

  const hasPartner = spouseRef != null && spouseRef !== '';
  const hasDonor = donors.length > 0;

  let conceivedWith = '';
  if (hasPartner && hasDonor) conceivedWith = 'partnerAndDonor';
  else if (hasPartner) conceivedWith = 'partner';
  else if (spermDonor && eggDonor) conceivedWith = 'embryoDonor';
  else if (spermDonor) conceivedWith = 'spermDonor';
  else if (eggDonor) conceivedWith = 'eggDonor';
  // else '' — unspecified, carrier-alone (never assume a donor)

  return {
    conceivedWith,
    donors,
    otherParentMemberId: hasPartner ? spouseRef : undefined
  };
}

// Biological-possibility check, shared by the FHQ form and the pedigree editor. The carrier is
// always biologically female here (only a gestational/female carrier reaches this UI), so she
// contributes the egg; the pregnancy still needs a sperm source. If a partner is explicitly
// NAMED, they must be able to provide it — a biologically-male partner — otherwise a sperm
// donor is mandatory (two biological females need a sperm donor). A fully UNSPECIFIED
// conception (no named partner) is left blank and NOT forced to a donor (never assume).
// Returns an error string to block save, or null.
export function conceptionValidationError({
  partnerSpecified = false,
  partnerGender = null,
  spermDonor = false
} = {}) {
  if (partnerSpecified && partnerGender !== 'male' && !spermDonor) {
    return 'A same-sex (non-male) partner needs a sperm donor — add a sperm donor, or choose a male partner.';
  }
  return null;
}

// Inverse: recover the two UI axes from a stored record, so the editor can seed its controls.
// Handles legacy 'embryoDonor' records that predate the donors[] array (treated as both donors).
export function conceptionToAxes(record = {}) {
  const donors = Array.isArray(record.donors) ? record.donors : [];
  const cw = record.conceivedWith;
  const partnerConceived = cw === 'partner' || cw === 'partnerAndDonor';
  const embryoNoDonors = cw === 'embryoDonor' && donors.length === 0;
  return {
    spouseRef: partnerConceived ? record.otherParentMemberId ?? null : null,
    spermDonor: donors.some((d) => d.role === 'sperm') || embryoNoDonors,
    eggDonor: donors.some((d) => d.role === 'ovum') || embryoNoDonors
  };
}
